import { createJobSlug } from "./slug";

/**
 * Utility functions for updating Open Graph and meta tags dynamically
 */

/**
 * Update Open Graph meta tags for a job
 * @param {Object} job - Job object with title, description, employer, etc.
 * @param {string} jobId - Job ID for building the URL
 */
export function updateJobMetaTags(job, jobId) {
  if (!job || !job.title) {
    return;
  }

  // Always use production domain for Open Graph, replace localhost
  let baseUrl = window.location.origin;
  if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
    baseUrl = 'https://applypa.com';
  }
  const jobSlug = createJobSlug(job.title, jobId);
  const jobUrl = `${baseUrl}/jobs/${jobSlug || jobId || ""}`.replace(/\/+$/, "");
  
  // Build title with employer name if available
  let title = job.title;
  if (job.employer?.name) {
    title = `${job.title} at ${job.employer.name}`;
  }

  // Build description - use first paragraph or truncate to 200 chars
  let description = '';
  if (job.description) {
    // Remove HTML tags if present
    const textDescription = job.description.replace(/<[^>]*>/g, '');
    
    // Try to get first paragraph (split by double newlines or periods)
    const firstParagraph = textDescription.split(/\n\n|\.\s+/)[0];
    
    // If first paragraph is too long, truncate to 200 chars
    if (firstParagraph && firstParagraph.length <= 200) {
      description = firstParagraph;
    } else {
      // Truncate to 200 chars and add ellipsis
      description = textDescription.substring(0, 200).trim();
      if (textDescription.length > 200) {
        description += '...';
      }
    }
  } else {
    description = 'Find this job and more on Applypa';
  }

  // Add job type and salary if available
  const jobInfo = [];
  if (job.job_type && job.job_type !== 'Not Specified') {
    jobInfo.push(job.job_type);
  }
  if (job.salary) {
    jobInfo.push(job.salary);
  }
  
  if (jobInfo.length > 0) {
    description = `${jobInfo.join(' • ')}. ${description}`;
  }

  // Get or create default image URL
  const imageUrl = `${baseUrl}/logo/applypa-logo.png`;

  // Update title tag
  updateMetaTag('title', title, 'tag');
  
  // Update description meta tag
  updateMetaTag('description', description, 'name');

  // Update Open Graph tags
  updateMetaTag('og:title', title, 'property');
  updateMetaTag('og:description', description, 'property');
  updateMetaTag('og:type', 'article', 'property');
  updateMetaTag('og:url', jobUrl, 'property');
  updateMetaTag('og:image', imageUrl, 'property');
  updateMetaTag('og:site_name', 'Applypa', 'property');

  // Update Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image', 'name');
  updateMetaTag('twitter:title', title, 'name');
  updateMetaTag('twitter:description', description, 'name');
  updateMetaTag('twitter:image', imageUrl, 'name');

  // Update article tags if available
  if (job.date_posted) {
    const publishedTime = new Date(job.date_posted).toISOString();
    updateMetaTag('article:published_time', publishedTime, 'property');
  }
  
  if (job.employer?.name) {
    updateMetaTag('article:author', job.employer.name, 'property');
  }
}

/**
 * Update a specific meta tag
 * @param {string} name - Meta tag name or property
 * @param {string} content - Content value
 * @param {string} type - 'name' or 'property' or 'tag' for title tag
 */
function updateMetaTag(name, content, type = 'name') {
  if (type === 'tag') {
    // Update title tag
    const titleTag = document.querySelector('title');
    if (titleTag) {
      titleTag.textContent = content;
    } else {
      // Create title tag if it doesn't exist
      const newTitle = document.createElement('title');
      newTitle.textContent = content;
      document.head.appendChild(newTitle);
    }
    return;
  }

  // Find existing meta tag
  let metaTag = document.querySelector(
    `meta[${type}="${name}"]`
  );

  if (metaTag) {
    // Update existing tag
    metaTag.setAttribute('content', content);
  } else {
    // Create new meta tag
    metaTag = document.createElement('meta');
    metaTag.setAttribute(type, name);
    metaTag.setAttribute('content', content);
    document.head.appendChild(metaTag);
  }
}

/**
 * Reset meta tags to default values
 */
export function resetMetaTags() {
  // Always use production domain for Open Graph, replace localhost
  let baseUrl = window.location.origin;
  if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
    baseUrl = 'https://applypa.com';
  }
  const defaultTitle = 'Applypa - Find Your Dream Job in the Philippines';
  const defaultDescription = 'Discover thousands of job opportunities in the Philippines. Apply to jobs from top employers and advance your career.';
  const defaultImage = `${baseUrl}/logo/applypa-logo.png`;

  updateMetaTag('title', defaultTitle, 'tag');
  updateMetaTag('description', defaultDescription, 'name');
  updateMetaTag('og:title', defaultTitle, 'property');
  updateMetaTag('og:description', defaultDescription, 'property');
  updateMetaTag('og:type', 'website', 'property');
  updateMetaTag('og:url', baseUrl, 'property');
  updateMetaTag('og:image', defaultImage, 'property');
  updateMetaTag('og:site_name', 'Applypa', 'property');
  updateMetaTag('twitter:card', 'summary_large_image', 'name');
  updateMetaTag('twitter:title', defaultTitle, 'name');
  updateMetaTag('twitter:description', defaultDescription, 'name');
  updateMetaTag('twitter:image', defaultImage, 'name');
}

